import { Link } from '../router/Link';
import { Thumbnail } from '../ui';
import type { PlaylistRow } from './model';
import styles from './listing.module.css';
import { useCopy } from '../strings/useCopy';

/**
 * A playlist in a results grid. Renders props (R13).
 *
 * The thumbnail is the *first video's* frame — `playlistThumbnail` is one bare `i.ytimg.com` URL, not
 * a size list — so the count sits on top of it as a badge, which is the only thing that distinguishes
 * this card from a video card at a glance. Without it a playlist reads as a video whose duration
 * failed to load.
 *
 * A count of zero is drawn as **no badge**, not as "0 videos": the same rule `videoBadgeOf` applies to
 * a duration. Search's playlist counts are genuine (17, 10, 38, 178, 279 measured), so this is the
 * rare branch rather than the usual one.
 */
export interface PlaylistCardProps {
  readonly item: PlaylistRow;
  readonly tabIndex: number;
  readonly onFocus?: () => void;
}

export function PlaylistCard({ item, tabIndex, onFocus }: PlaylistCardProps) {
  const words = useCopy().text.listing;
  return (
    <li className={styles.card} onFocus={onFocus}>
      <Link className={styles.thumbLink} href={item.href} tabIndex={-1} ariaHidden>
        <Thumbnail src={item.thumbnailUrl} className={styles.thumb} />
        {item.videoCountText === null ? null : (
          <span className={styles.playlistCount} aria-hidden="true">
            {words.meta.videos(item.videoCountText)}
          </span>
        )}
      </Link>

      <div className={styles.text}>
        <Link className={styles.title} href={item.href} title={item.title} tabIndex={tabIndex}>
          {item.title}
        </Link>

        <div className={styles.byline}>
          {item.authorHref === null ? (
            <span className={styles.author} title={item.author}>
              {item.author}
            </span>
          ) : (
            <Link className={styles.author} href={item.authorHref} title={item.author} tabIndex={tabIndex}>
              {item.author}
            </Link>
          )}
          {item.authorVerified ? (
            <span className={styles.verified} title={words.verified}>
              ✓
            </span>
          ) : null}
        </div>

        <div className={styles.meta}>
          {/* Stated in the text too, because the badge is `aria-hidden` — it is decoration over an
              image, and a screen reader needs the fact in the flow rather than on the picture. */}
          <span className={styles.playlistTag}>
            {item.videoCountText === null
              ? words.playlistTag
              : words.playlistTagWithCount(item.videoCountText)}
          </span>
        </div>
      </div>
    </li>
  );
}
